import React from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Square, Circle, Triangle } from 'lucide-react';

export default function GraphicElements() {
  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-4xl md:text-5xl font-display font-normal text-jackstone-blue mb-4">
          Graphic Elements
        </h1>
        <p className="text-base text-body leading-relaxed">
          Supporting graphic elements enhance our brand identity with industrial precision and modern aesthetics.
        </p>
      </div>

      <Card className="p-8 bg-surface">
        <h2 className="text-2xl font-heading font-semibold text-jackstone-blue mb-4">
          Icon Style
        </h2>
        <p className="text-body mb-6">
          Icons should be geometric, clean, and professional with a technical aesthetic.
        </p>
        <div className="grid grid-cols-3 gap-6">
          <div className="flex flex-col items-center">
            <div className="w-16 h-16 bg-jackstone-blue rounded-lg flex items-center justify-center mb-3">
              <Square className="h-8 w-8 text-white" strokeWidth={2} />
            </div>
            <p className="text-sm text-center text-subtle">Geometric shapes</p>
          </div>
          <div className="flex flex-col items-center">
            <div className="w-16 h-16 bg-navy rounded-lg flex items-center justify-center mb-3">
              <Circle className="h-8 w-8 text-white" strokeWidth={2} />
            </div>
            <p className="text-sm text-center text-subtle">2px stroke weight</p>
          </div>
          <div className="flex flex-col items-center">
            <div className="w-16 h-16 bg-accent-teal rounded-lg flex items-center justify-center mb-3">
              <Triangle className="h-8 w-8 text-white" strokeWidth={2} />
            </div>
            <p className="text-sm text-center text-subtle">Rounded corners</p>
          </div>
        </div>
      </Card>

      <div>
        <h2 className="text-2xl font-heading font-semibold text-jackstone-blue mb-4">
          Pattern & Texture Guidelines
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          <Card className="p-6">
            <Badge className="mb-3">Grid Pattern</Badge>
            <div className="h-32 industrial-grid rounded-lg mb-4"></div>
            <p className="text-sm text-subtle">
              Subtle grid patterns can be used as background elements, evoking technical drawings
              and engineering precision.
            </p>
          </Card>

          <Card className="p-6">
            <Badge className="mb-3">Linear Elements</Badge>
            <div className="h-32 bg-surface rounded-lg mb-4 flex items-center justify-center">
              <div className="space-y-3 w-full px-6">
                <div className="h-1 bg-jackstone-blue rounded-full w-3/4"></div>
                <div className="h-1 bg-jackstone-blue rounded-full w-1/2"></div>
                <div className="h-1 bg-jackstone-blue rounded-full w-2/3"></div>
              </div>
            </div>
            <p className="text-sm text-subtle">
              Horizontal lines and dividers create structure and emphasize hierarchy.
            </p>
          </Card>
        </div>
      </div>

      <Card className="p-6">
        <h3 className="text-xl font-heading font-semibold text-jackstone-blue mb-4">
          Illustration Style
        </h3>
        <p className="text-body mb-4">
          When illustrations are needed, they should follow these principles:
        </p>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <h4 className="font-medium text-body">Style Characteristics</h4>
            <ul className="space-y-1 text-sm text-subtle">
              <li>• Clean, vector-based graphics</li>
              <li>• Isometric or technical drawing style</li>
              <li>• Limited color palette (brand colors only)</li>
              <li>• Minimal shading, flat or subtle gradients</li>
            </ul>
          </div>
          <div className="space-y-2">
            <h4 className="font-medium text-body">Usage Guidelines</h4>
            <ul className="space-y-1 text-sm text-subtle">
              <li>• Technical diagrams and exploded views</li>
              <li>• Process flow illustrations</li>
              <li>• Infographic elements</li>
              <li>• System architecture diagrams</li>
            </ul>
          </div>
        </div>
      </Card>

      <div>
        <h2 className="text-2xl font-heading font-semibold text-jackstone-blue mb-4">
          Background Treatments
        </h2>
        <div className="grid md:grid-cols-3 gap-6">
          <Card className="overflow-hidden">
            <div className="h-32 bg-gradient-to-br from-jackstone-blue to-navy"></div>
            <div className="p-4">
              <h4 className="font-display font-normal text-body mb-1">Subtle Gradient</h4>
              <p className="text-xs text-subtle">
                Primary to dark, 135° angle for depth
              </p>
            </div>
          </Card>

          <Card className="overflow-hidden">
            <div className="h-32 bg-surface"></div>
            <div className="p-4">
              <h4 className="font-display font-normal text-body mb-1">Neutral Solid</h4>
              <p className="text-xs text-subtle">
                Neutral surface for clean, spacious sections
              </p>
            </div>
          </Card>

          <Card className="overflow-hidden">
            <div className="h-32 bg-light-blue"></div>
            <div className="p-4">
              <h4 className="font-display font-normal text-body mb-1">Soft Accent</h4>
              <p className="text-xs text-subtle">
                Light blue for highlighted content areas
              </p>
            </div>
          </Card>
        </div>
      </div>

      <Card className="p-6 bg-light-blue border-jackstone-blue/20">
        <h3 className="text-xl font-heading font-semibold text-body mb-3">
          Shape Language
        </h3>
        <p className="text-body mb-4">
          Our shape language emphasizes rectangular forms with slightly rounded corners (8px radius),
          reflecting the precision and approachability of our brand.
        </p>
        <div className="flex gap-4">
          <div className="flex-1 h-20 bg-jackstone-blue rounded-lg"></div>
          <div className="flex-1 h-20 bg-navy rounded-lg"></div>
          <div className="flex-1 h-20 bg-accent-teal rounded-lg"></div>
        </div>
      </Card>
    </div>
  );
}
